import React, { useEffect, memo, useCallback, useMemo } from 'react';
import { Field, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useRouter } from 'next/router';
import {
  SearchBar,
  SearchBarCity,
  SearchBarLocations,
  SearchBarPlaceholder,
  SearchBarText
} from './styled';
import {
  selectSearchFormLocationIds,
  selectSearchLocationNames
} from '../../../../store/selectors';
import { setChosenLocationNames, setError } from '../../../../actions';
import { ModalParams, ModalTypeEnum } from '../../NewSearchBar/types';
import { ANY_CITY_ID, Cities } from '../../../../utils/helpers/constants';
import { useCityAndLocationsQuery } from '../../../../hooks/useCityAndLocationsQuery';
import { getChosenLocationNames } from '../../../../utils/helpers/getChosenLocationNames';
import { LocationScopeEnum } from '../../../../graphql/types';

interface FilterLocationsProps {
  setModalParams?: React.Dispatch<React.SetStateAction<ModalParams>>;
  handleOpenLocations?: () => void;
  modalScreen?: ModalTypeEnum;
}

export const FilterLocations = memo(
  ({
    setModalParams,
    handleOpenLocations,
    modalScreen
  }: FilterLocationsProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const router = useRouter();
    const { values } = useFormState({
      subscription: { values: true }
    });
    const chosenLocationNames = useSelector(
      selectSearchLocationNames,
      shallowEqual
    );
    const storedLocationIds: string[] = useSelector(
      selectSearchFormLocationIds,
      shallowEqual
    );
    const cityId = values.city_id || ANY_CITY_ID;

    const isHomePageAndBakuCity =
      router.pathname === '/' && cityId === Cities.Baku.id;

    const { data, error, loading } = useCityAndLocationsQuery(
      cityId,
      LocationScopeEnum.All,
      1000,
      isHomePageAndBakuCity &&
        (!modalScreen || modalScreen !== ModalTypeEnum.Filters)
    );

    useEffect(() => {
      if (error) dispatch(setError(true));
    }, [error]);

    const toggleHandlerLocations = useCallback(() => {
      const hasLocationOrCitySelected =
        chosenLocationNames?.length > 0 ||
        (cityId &&
          cityId !== ANY_CITY_ID &&
          (data?.locations || []).length > 0);

      if (hasLocationOrCitySelected || isHomePageAndBakuCity) {
        setModalParams?.({
          initialScreen: ModalTypeEnum.Locations
        });
      } else {
        setModalParams?.({
          initialScreen: ModalTypeEnum.City
        });
      }
    }, [
      chosenLocationNames,
      cityId,
      data?.locations,
      isHomePageAndBakuCity,
      setModalParams
    ]);

    const openHandler = useMemo(
      () => handleOpenLocations || toggleHandlerLocations,
      [handleOpenLocations, toggleHandlerLocations]
    );

    useEffect(() => {
      dispatch(
        setChosenLocationNames(
          getChosenLocationNames(data?.locations || [], storedLocationIds)
        )
      );
    }, [data?.locations, storedLocationIds]);

    const cityName =
      data && data.city !== null
        ? data.city?.name
        : t('search.city_search.any');

    const bakuCityName = t('search.city_search.baku');

    const isLocationsValue =
      cityId !== ANY_CITY_ID && chosenLocationNames.length && !loading;

    return (
      <SearchBar
        tabIndex={0}
        role="button"
        onClick={openHandler}
        onKeyPress={openHandler}
        data-stat="new-search-search-bar-locations"
      >
        <Field name="location_ids" component="input" type="hidden" />
        <SearchBarText>
          {isLocationsValue ? (
            <SearchBarLocations data-cy="chosen-locations-names">
              {chosenLocationNames.join(', ')}
            </SearchBarLocations>
          ) : (
            <SearchBarPlaceholder
              data-cy="chosen-locations-names-title"
              data-stat="chosen-locations-names-title"
            >
              {t('search.locations_search.locations_placeholder.all_groups')}
            </SearchBarPlaceholder>
          )}

          {!loading ? (
            <SearchBarCity
              tag="body_1"
              data-cy="chosen-locations-names-city"
              data-stat="chosen-locations-names-city"
            >
              {cityId === Cities.Baku.id ? bakuCityName : cityName}
            </SearchBarCity>
          ) : (
            <SearchBarPlaceholder data-cy="chosen-locations-names-title">
              {t('search.city_search.title')}
            </SearchBarPlaceholder>
          )}
        </SearchBarText>
      </SearchBar>
    );
  }
);

FilterLocations.displayName = 'FilterLocations';
